<template>
  <b-modal
    id="review-report-modal"
    ref="modalRef"
    size="lg"
    centered
    hide-footer
    title="Review reported content"
    body-class="p-0"
    @hidden="resetModal"
  >
    <template v-if="report">
      <div v-if="step === 1">
        <div class="px-4 py-3">
          <h6 class="mb-3">Content</h6>
          <div class="review-report-modal__object">
            <div class="d-flex justify-content-between align-items-center">
              <strong>{{ report.author_name || 'Unknown author' }}</strong>
              <span v-if="report.posted_at" class="text-muted review-report-modal__posted-at">
                {{ formatDate(report.posted_at) }}
              </span>
            </div>
            <div class="d-flex align-items-baseline flex-wrap mt-1">
              <b-badge pill variant="light" class="mr-2">{{ entityLabel }}</b-badge>
              <b-badge
                v-if="report.is_hidden && report.status !== CONTENT_REPORT_STATUSES.PENDING"
                pill
                variant="danger"
                class="mr-2"
                >Hidden</b-badge
              >
              <span class="review-report-modal__content">
                {{ report.content_preview || '(no text content)' }}
              </span>
            </div>
          </div>
        </div>

        <hr class="m-0" />

        <div class="px-4 py-3">
          <h6 class="mb-3">{{ report.report_count }} {{ report.report_count === 1 ? 'report' : 'reports' }}</h6>
          <app-loading :loading="isLoadingDetails" />
          <template v-if="!isLoadingDetails">
            <div
              v-for="individualReport in individualReports"
              :key="individualReport.recognition_content_report_id"
              class="review-report-modal__report mb-2"
            >
              <div class="d-flex justify-content-between">
                <strong>{{ individualReport.reporter_name || 'Unknown reporter' }}</strong>
                <span class="text-muted">{{ formatDate(individualReport.created_at) }}</span>
              </div>
              <div>
                <b-badge pill variant="light" class="mr-1">
                  {{ categoryLabel(individualReport.category) }}
                </b-badge>
                <span v-if="individualReport.reason" class="review-report-modal__reason">
                  "{{ individualReport.reason }}"
                </span>
              </div>
            </div>
          </template>
          <b-alert :show="!!errorMessage" variant="danger" class="mt-2 mb-0">
            {{ errorMessage }}
          </b-alert>
        </div>

        <div class="px-4 py-3 footer-container">
          <template v-if="report.is_hidden">
            <b-button variant="danger" :disabled="isSubmitting" @click="onKeepHidden">
              <b-spinner v-if="isSubmitting && pendingAction === CONTENT_REPORT_ACTIONS.HIDE" small class="mr-1" />
              Keep hidden
            </b-button>
            <b-button variant="outline-primary" :disabled="isSubmitting" @click="onReinstate">
              <b-spinner v-if="isSubmitting && pendingAction === CONTENT_REPORT_ACTIONS.RESTORE" small class="mr-1" />
              Reinstate content
            </b-button>
          </template>
          <template v-else>
            <b-button variant="outline-secondary" :disabled="isSubmitting" @click="onDismiss">
              <b-spinner v-if="isSubmitting && pendingAction === CONTENT_REPORT_ACTIONS.DISMISS" small class="mr-1" />
              Dismiss reports
            </b-button>
            <b-button variant="danger" :disabled="isSubmitting" @click="onHide">
              <b-spinner v-if="isSubmitting && pendingAction === CONTENT_REPORT_ACTIONS.HIDE" small class="mr-1" />
              Hide content
            </b-button>
          </template>
        </div>
      </div>

      <div v-else-if="step === 'success'" class="px-4 py-5 text-center">
        <div class="success-icon">
          <font-awesome-icon :icon="['fal', 'circle-check']" />
        </div>
        <p class="mt-3">{{ successMessage }}</p>
        <div class="footer-container footer-container--right">
          <b-button variant="primary" @click="onDone">Done</b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import AppLoading from '@/components/AppLoading.vue';
import { actionRecognitionContentReport, getRecognitionContentReportsByEntity } from '@/queries/recognition';
import {
  CONTENT_REPORT_ACTIONS,
  CONTENT_REPORT_CATEGORY_LABELS,
  CONTENT_REPORT_ENTITY_LABELS,
  CONTENT_REPORT_STATUSES,
} from '@/pages/recognition/constants';

const props = defineProps({
  report: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['actioned']);

const modalRef = ref(null);
const step = ref(1);
const pendingAction = ref(null);
const errorMessage = ref('');
const successMessage = ref('');
const individualReports = ref([]);
const isLoadingDetails = ref(false);

const queryClient = useQueryClient();

// Fetch imperatively (per-open) via fetchQuery on a cache-disabled query
// (gcTime/staleTime 0) rather than a reactive useQuery: a cached, entity-keyed
// query was surfacing a stale error from a prior failed fetch and not
// re-issuing a request on re-open.
//
// Takes the report explicitly rather than reading props.report: the parent
// sets the :report prop and calls show() in the same tick, but Vue propagates
// the prop to this child asynchronously, so props.report is still null (first
// open) or the previously-selected row (later opens) when show() runs.
const loadDetails = async report => {
  if (!report) {
    return;
  }
  isLoadingDetails.value = true;
  try {
    individualReports.value = await queryClient.fetchQuery(
      getRecognitionContentReportsByEntity({ entityType: report.entity_type, entityId: report.entity_id }),
    );
  } catch (e) {
    individualReports.value = [];
    errorMessage.value = 'Could not load report details.';
  } finally {
    isLoadingDetails.value = false;
  }
};

const actionMutation = useMutation(actionRecognitionContentReport());
const isSubmitting = computed(() => actionMutation.isPending.value);

const entityLabel = computed(() => CONTENT_REPORT_ENTITY_LABELS[props.report?.entity_type] || 'Content');

const categoryLabel = category => CONTENT_REPORT_CATEGORY_LABELS[category] || category || 'Other';

const formatDate = dateStr => {
  if (!dateStr) {
    return '-';
  }
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const submitAction = (actionType, successText) => {
  pendingAction.value = actionType;
  errorMessage.value = '';
  actionMutation.mutate(
    { entityType: props.report.entity_type, entityId: props.report.entity_id, actionType },
    {
      onSuccess: () => {
        successMessage.value = successText;
        step.value = 'success';
      },
      onError: () => {
        errorMessage.value = 'Something went wrong. Please try again.';
      },
      onSettled: () => {
        pendingAction.value = null;
      },
    },
  );
};

const onDismiss = () => submitAction(CONTENT_REPORT_ACTIONS.DISMISS, 'Reports dismissed. The content remains visible.');
const onHide = () => submitAction(CONTENT_REPORT_ACTIONS.HIDE, 'Content hidden from the Recognition feed.');
const onReinstate = () => submitAction(CONTENT_REPORT_ACTIONS.RESTORE, 'Content reinstated to the Recognition feed.');
const onKeepHidden = () => submitAction(CONTENT_REPORT_ACTIONS.HIDE, 'Content kept hidden.');

const onDone = () => {
  emit('actioned');
  modalRef.value?.hide();
};

const resetModal = () => {
  step.value = 1;
  errorMessage.value = '';
  successMessage.value = '';
  individualReports.value = [];
};

const show = report => {
  modalRef.value?.show();
  // Clear any stale error from a previous open, then fetch on every open
  // (re-reviewing the same row passes an unchanged object reference).
  errorMessage.value = '';
  loadDetails(report);
};

defineExpose({ show });
</script>

<style lang="scss" scoped>
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.review-report-modal {
  &__object {
    background-color: var(--palette-color-default-lighten-95);
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
  }

  &__posted-at {
    font-size: 0.875rem;
    white-space: nowrap;
  }

  &__content {
    white-space: pre-wrap;
  }

  &__report {
    background-color: var(--palette-color-default-lighten-95);
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
  }

  &__reason {
    font-style: italic;
  }
}

.success-icon {
  font-size: 4rem;
  color: var(--palette-color-core-5);
}

.footer-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--palette-color-default-lighten-90);

  &--right {
    justify-content: flex-end;
    // Success step has no separator line above its single "Done" button.
    border-top: none;
  }
}

.footer-container :deep(.btn) {
  white-space: nowrap;
  min-width: max-content;
  flex: 0 0 auto;
}
</style>
